import {
  GoAButton,
  GoAButtonGroup,
  GoADropdown,
  GoADropdownItem,
  GoAFormItem,
  GoAModal,
  GoARadioGroup,
  GoARadioItem,
  GoASpacer,
} from '@abgov/react-components';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import {
  defaultValidate,
  WizardButtonGroup,
} from '../wizard/wizard-button-group-component';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../state/store';
import {
  publishCollision,
  updateCollision,
} from '../../state/slices/collisionSlice';
import { Collision } from '../../models/collision';
import {
  pushNotification,
  removeAllValidationErrorNotifications,
} from '../../state/slices/notificationSlice';

import {
  AnimalType,
  getConfigurationByDescription,
  isStruckObject,
  setCollisionSubTypeUpdated,
} from '../../state/slices/configurationSlice';
import { set } from 'lodash';
import { NotificationType } from '../notifications/notification-component';
import { IsInReviewEditMode } from '../wizard/wizard-component';

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

export interface AnimalComponentProps extends BaseWizardProps {}
export const AnimalComponet = ({
  navigationConfiguration,
}: AnimalComponentProps) => {
  const dispatch = useAppDispatch();
  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );

  const stateCollision = useAppSelector((state: RootState) => state.collision);

  const defaultSelection = '0';
  const headerErrorText = 'Choose one animal type';
  const dropDownErrorText = 'Choose one animal type from the drop-down menu';
  const errorText = 'Choose one animal type above';
  const [formError, setFormError] = useState('');

  const [selection, setSelection] = useState(defaultSelection);
  const [otherAnimalId, setOtherAnimalId] = useState('');
  const [otherAnimal, setOtherAnimal] = useState('');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [modalHeading, setModalHeading] = useState('');
  const [nextPage, setNextPage] = useState('');

  useEffect(() => {
    const other = getConfigurationByDescription(
      stateConfiguration.configuration.animalType,
      AnimalType.OTHER,
    );

    setOtherAnimalId(other?.id.toString() ?? '');
  }, []);

  useEffect(() => {
    if (stateCollision.collision && stateCollision.collision?.animalType) {
      const foundAnimalType = stateConfiguration.configuration.animalType.find(
        (x) => x.id == stateCollision.collision.animalType,
      );

      if (foundAnimalType && foundAnimalType.description != AnimalType.OTHER) {
        setSelection(stateCollision.collision.animalType.toString());
      } else {
        setSelection(otherAnimalId);
        setOtherAnimal(stateCollision.collision.animalType.toString());
      }
    }
  }, [stateCollision, otherAnimalId]);

  useEffect(() => {
    const pageConfig = navigationConfiguration.pagingConfiguration.find(
      (item) => item.default !== undefined,
    );

    setNextPage(pageConfig?.default ?? 'error');
  }, []);

  useEffect(() => {
    if (
      stateConfiguration.uiTriggers.collisionTypeUpdated ||
      stateConfiguration.uiTriggers.collisionSubTypeUpdated
    ) {
      setModalHeading('Your collision type changes have been saved');
    } else {
      setModalHeading('');
    }
  }, [
    stateConfiguration.uiTriggers.collisionTypeUpdated,
    stateConfiguration.uiTriggers.collisionSubTypeUpdated,
  ]);

  const addOtherAnimalOptions = (): JSX.Element[] => {
    const options: JSX.Element[] = [];

    stateConfiguration.configuration.otherAnimalType.forEach((animal) => {
      options.push(
        <GoADropdownItem
          name="otherAnimalType"
          key={animal.id}
          value={animal.id.toString()}
          label={animal.formLabel}
        />,
      );
    });

    return options;
  };

  const validate = (): boolean => {
    let valid = true;
    if (selection == otherAnimalId && otherAnimal == '') {
      valid = false;
    }

    return defaultValidate(
      selection,
      defaultSelection,
      selection == otherAnimalId ? dropDownErrorText : errorText,
      setFormError,
      () => {
        dispatch(removeAllValidationErrorNotifications());
        dispatch(
          pushNotification({
            body: headerErrorText,
            notificationType: NotificationType.importantValidationError,
          }),
        );
      },
      valid,
    );
  };

  const navigate = (page: string) => {
    if (IsInReviewEditMode()) {
      setShowReviewModal(true);
    } else {
      if (navigationConfiguration.navigate) {
        navigationConfiguration.navigate(page);
      }
    }
  };

  const save = () => {
    const primaryEventType =
      stateConfiguration.configuration!.primaryEvent.find((x) => {
        return isStruckObject(x.id, stateConfiguration);
      });

    const objectIdentificationType =
      stateConfiguration.configuration!.objectIdentification.find((x) => {
        return x.value == 'Animal';
      });

    const selectedAnimal = parseInt(
      selection != otherAnimalId ? selection : otherAnimal,
    );

    let updatedCollision: Collision = { ...stateCollision.collision };

    if (
      navigationConfiguration.updateMetadata &&
      navigationConfiguration.stepId
    ) {
      const stepUpdated = navigationConfiguration.updateMetadata(
        updatedCollision,
        navigationConfiguration.stepId!,
        true,
        false,
      );

      if (stepUpdated && updatedCollision.metadata) {
        updatedCollision.metadata = {
          ...updatedCollision.metadata,
          formState: stepUpdated,
        };
      }
    }
    const prevAnimalType = updatedCollision.animalType;
    updatedCollision.primaryEvent = primaryEventType?.id ?? 0;
    updatedCollision.objectIdentification = objectIdentificationType?.id ?? 0;
    updatedCollision.animalType = selectedAnimal;
    updatedCollision.witnessOrCameraAvailable = null;

    dispatch(updateCollision(updatedCollision));

    if (navigationConfiguration.save) {
      navigationConfiguration.save();
      if (IsInReviewEditMode() && prevAnimalType != updatedCollision.animalType)
        dispatch(setCollisionSubTypeUpdated(true));
    }
  };

  return (
    <div>
      <GoAModal
        calloutVariant="important"
        actions={
          <GoAButtonGroup alignment="end">
            <GoAButton
              onClick={() => {
                setShowReviewModal(false);
              }}
              type="tertiary"
            >
              Cancel
            </GoAButton>
            <GoAButton
              onClick={() => {
                setShowReviewModal(false);
                navigationConfiguration.navigate('#review');
              }}
              type="primary"
            >
              View report summary
            </GoAButton>
          </GoAButtonGroup>
        }
        heading={<div>{modalHeading}</div>}
        open={showReviewModal}
      >
        <div>
          Click 'View report summary to return to the report review page.
        </div>
      </GoAModal>
      <h1>Collision type</h1>
      The collision details section is the starting point for drivers to
      identify the type of collision they were involved in. In some cases you'll
      be prompted to add some additional details depending on your type of
      collision.
      <h2>My vehicle hit an animal</h2>
      <p>Choose one of the animal types below</p>
      <GoASpacer vSpacing="2xs" />
      <GoAFormItem testId="animal-form" error={formError}>
        <GoARadioGroup
          name="collisionTypeGroup"
          error={formError.length > 0}
          value={selection}
          testId="animal-radio-group"
          onChange={(item, id) => {
            setSelection(id);
            setFormError('');
            dispatch(removeAllValidationErrorNotifications());
          }}
        >
          {stateConfiguration.configuration.animalType.map((animal) => {
            return (
              <GoARadioItem
                key={animal.id}
                value={animal.id.toString()}
                label={animal.formLabel}
                testId={
                  animal.id.toString() == otherAnimalId
                    ? 'other-animal-radio'
                    : ''
                }
              ></GoARadioItem>
            );
          })}
        </GoARadioGroup>
        <div
          style={{
            display: otherAnimalId == selection ? 'block' : 'none',
          }}
        >
          <GoAFormItem label="Other animal types">
            <GoADropdown
              name="otherAnimalType"
              testId="other-animal-dropdown"
              placeholder="--Select---"
              value={otherAnimal}
              onChange={(item, id) => {
                setFormError('');
                dispatch(removeAllValidationErrorNotifications());
                setOtherAnimal(id.toString());
              }}
            >
              {addOtherAnimalOptions()}
            </GoADropdown>
          </GoAFormItem>
        </div>
      </GoAFormItem>
      <WizardButtonGroup
        nextPage={nextPage}
        validate={(): boolean => {
          const isValid = validate();
          return isValid;
        }}
        showViewSummaryButton={!IsInReviewEditMode()}
        goToReview={navigationConfiguration.goToReview}
        save={save}
        navigate={navigate}
      />
    </div>
  );
};
